/**
 * Рейка команд слева (редактор плана, конструктор мебели): растёт по содержимому и убирает
 * непоместившиеся команды в меню «Ещё». Сложить и раскрыть подписи — плавно: ширина рейки
 * едет от старой к новой (FLIP), пока она едет, подписи не переносятся по словам.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { IconButton } from "./IconButton";
import { IconMore, IconToolRail } from "./icons";
import { Modal } from "./Modal";
import { Text } from "./Text";

export interface ToolRailItem {
	icon: ReactNode;
	label: string;
	tooltip: string;
	active?: boolean;
	disabled?: boolean;
	onClick: () => void;
}

interface Props {
	items: ToolRailItem[];
	disabled?: boolean;
	labelsVisible: boolean;
	onToggleLabels: () => void;
}

/** Сколько едет ширина рейки, мс */
const RAIL_MOTION_MS = 240;

export const ToolRail = ({ items, disabled = false, labelsVisible, onToggleLabels }: Props) => {
	const railRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLDivElement>(null);
	const toggleRef = useRef<HTMLDivElement>(null);
	const [visibleItemCount, setVisibleItemCount] = useState(Number.POSITIVE_INFINITY);
	const [overflowOpen, setOverflowOpen] = useState(false);
	const [animating, setAnimating] = useState(false);
	/** Подписи и ширина рейки при прошлом замере: с чего ехать */
	const last = useRef<{ labels: boolean; width: number } | null>(null);

	// Подписи сложили или раскрыли: ширина едет от прежней к новой
	useLayoutEffect(() => {
		const rail = railRef.current;
		if (!rail) return;
		const before = last.current;
		const to = rail.getBoundingClientRect().width;
		last.current = { labels: labelsVisible, width: to };
		const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (!before || before.labels === labelsVisible || reduced) return;
		const from = before.width;
		if (Math.abs(from - to) < 1) return;
		setAnimating(true);
		rail.style.width = `${from}px`;
		// Замер заставляет браузер применить стартовую ширину до перехода
		rail.getBoundingClientRect();
		rail.style.transition = `width ${RAIL_MOTION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
		rail.style.width = `${to}px`;
		const done = () => {
			rail.style.transition = "";
			rail.style.width = "";
			setAnimating(false);
		};
		const timer = window.setTimeout(done, RAIL_MOTION_MS + 50);
		return () => {
			window.clearTimeout(timer);
			done();
		};
	}, [labelsVisible]);

	useLayoutEffect(() => {
		const rail = railRef.current;
		const measure = measureRef.current;
		const container = rail?.parentElement;
		if (!rail || !measure || !container) return;

		const updateVisibleItemCount = () => {
			const itemCount = items.length;
			const toggleHeight = toggleRef.current?.getBoundingClientRect().height ?? 0;
			const availableHeight =
				(rail.parentElement?.clientHeight ?? rail.clientHeight) - 18 - toggleHeight;
			const itemHeights = Array.from(
				measure.children,
				(item) => item.getBoundingClientRect().height,
			);
			if (itemHeights.length !== itemCount) return;
			const moreHeight = itemHeights[0] ?? 0;
			let usedHeight = 0;
			let count = 0;
			for (const height of itemHeights) {
				const gap = count === 0 ? 0 : 2;
				const remaining = itemHeights.length - count - 1;
				const needsMoreButton = remaining > 0;
				const moreGap = needsMoreButton ? 2 : 0;
				if (
					usedHeight + gap + height + moreGap + (needsMoreButton ? moreHeight : 0) >
					availableHeight
				)
					break;
				usedHeight += gap + height;
				count += 1;
			}
			setVisibleItemCount(count);
		};

		const observer = new ResizeObserver(updateVisibleItemCount);
		observer.observe(rail);
		observer.observe(container);
		observer.observe(measure);
		if (toggleRef.current) observer.observe(toggleRef.current);
		updateVisibleItemCount();
		return () => observer.disconnect();
	}, [items.length]);

	const visibleItems = items.slice(0, visibleItemCount);
	const overflowItems = items.slice(visibleItemCount);
	const labelPosition = labelsVisible ? "right" : "bottom";
	const itemLabel = (item: ToolRailItem) => (labelsVisible ? item.label : undefined);
	const itemTooltip = (item: ToolRailItem) =>
		labelsVisible ? undefined : { label: item.tooltip, side: "right" as const };
	const selectOverflowItem = (item: ToolRailItem) => {
		item.onClick();
		setOverflowOpen(false);
	};

	return (
		<>
			<div
				ref={railRef}
				className="flex max-h-full min-h-0 w-max flex-col gap-0.5 overflow-hidden rounded-lg border border-border bg-bg p-2 shadow-md"
			>
				<div ref={toggleRef}>
					<IconButton
						icon={<IconToolRail isOpen={labelsVisible} />}
						label={labelsVisible ? "Сложить" : undefined}
						labelPosition="right"
						tooltip={labelsVisible ? undefined : { label: "Показать текст", side: "right" }}
						aria-expanded={labelsVisible}
						className="text-ink"
						labelClassName="font-bold text-ink"
						onClick={onToggleLabels}
					/>
				</div>
				{visibleItems.map((item) => (
					<IconButton
						key={item.label}
						icon={item.icon}
						label={itemLabel(item)}
						tooltip={itemTooltip(item)}
						active={item.active}
						disabled={disabled || item.disabled}
						wrapLabel={!animating}
						labelPosition={labelPosition}
						className="text-ink"
						labelClassName="font-bold text-ink"
						onClick={item.onClick}
					/>
				))}
				{overflowItems.length > 0 && (
					<IconButton
						icon={<IconMore />}
						label={labelsVisible ? "Ещё" : undefined}
						tooltip={labelsVisible ? undefined : { label: "Ещё инструменты", side: "right" }}
						active={overflowOpen}
						disabled={disabled}
						wrapLabel
						labelPosition={labelPosition}
						className="text-ink"
						labelClassName="font-bold text-ink"
						onClick={() => setOverflowOpen(true)}
					/>
				)}
			</div>
			<div
				ref={measureRef}
				className="pointer-events-none invisible fixed top-0 -left-full flex w-max flex-col gap-0.5 p-2"
				aria-hidden="true"
			>
				{items.map((item) => (
					<IconButton
						key={item.label}
						icon={item.icon}
						label={itemLabel(item)}
						wrapLabel
						labelPosition={labelPosition}
						labelClassName="font-bold text-ink"
					/>
				))}
			</div>
			{overflowOpen && (
				<Modal
					ariaLabel="Остальные инструменты"
					className="p-4 md:max-w-sm"
					onClose={() => setOverflowOpen(false)}
				>
					<div className="mb-3 flex items-center justify-between">
						<Text as="span" className="text-sm font-semibold text-ink">
							Ещё инструменты
						</Text>
						<button
							type="button"
							className="text-ink transition-colors hover:text-ink"
							onClick={() => setOverflowOpen(false)}
							aria-label="Закрыть"
						>
							✕
						</button>
					</div>
					<div className="grid grid-cols-2 gap-2">
						{overflowItems.map((item) => (
							<IconButton
								key={item.label}
								className="min-h-18"
								icon={item.icon}
								label={item.label}
								active={item.active}
								disabled={disabled || item.disabled}
								wrapLabel
								labelClassName="font-bold text-ink"
								onClick={() => selectOverflowItem(item)}
							/>
						))}
					</div>
				</Modal>
			)}
		</>
	);
};
